/**
 * LocationPickerModal - Google Maps-based location selection modal
 * Features cascading dropdowns for Province > District > Municipality
 */
import { X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { config } from '../../services/config.js';

// Dynamic Google Maps script loader
const loadGoogleMaps = (apiKey) => {
    return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
            resolve(window.google.maps);
            return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        
        script.onload = () => {
            resolve(window.google.maps);
        };
        
        script.onerror = () => {
            reject(new Error('Failed to load Google Maps API'));
        };
        
        document.head.appendChild(script);
    });
};

const LocationPickerModal = ({
    formData,
    mapCenter,
    searchQuery,
    onSearchChange,
    onMapClick,
    onCoordinateChange,
    onAddressChange,
    getLocationFromCoordinates,
    onClose,
}) => {
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchTimeoutRef = useRef(null);
    const autocompleteServiceRef = useRef(null);
    const placesServiceRef = useRef(null);
    // Disable body scroll when modal is open
    useEffect(() => {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        document.documentElement.style.overflow = 'hidden';
        
        return () => {
            document.body.style.overflow = 'unset';
            document.body.style.paddingRight = '0px';
            document.documentElement.style.overflow = 'unset';
        };
    }, []);

    // Helper functions to validate and correct coordinates
    const getCorrectLatitude = (lat, lng) => {
        if (!lat || !lng) return lat;
        
        const latNum = parseFloat(lat);
        const lngNum = parseFloat(lng);
        
        // Nepal latitude range: ~26-30°N
        // Nepal longitude range: ~80-88°E
        if (latNum > 30 || latNum < 26) {
            // If lat is outside Nepal range, it might be longitude
            if (lngNum >= 26 && lngNum <= 30) {
                // Swap them - lat is actually longitude, lng is actually latitude
                console.log('Display corrected latitude:', lngNum, '(was showing as longitude)');
                return lngNum;
            }
        }
        return latNum;
    };
    
    const getCorrectLongitude = (lat, lng) => {
        if (!lat || !lng) return lng;
        
        const latNum = parseFloat(lat);
        const lngNum = parseFloat(lng);
        
        // Nepal longitude range: ~80-88°E
        // Nepal latitude range: ~26-30°N
        if (lngNum < 80 || lngNum > 88) {
            // If lng is outside Nepal range, it might be latitude
            if (latNum >= 80 && latNum <= 88) {
                // Swap them - lng is actually latitude, lat is actually longitude
                console.log('Display corrected longitude:', latNum, '(was showing as latitude)');
                return latNum;
            }
        }
        return lngNum;
    };

    const [locationData, setLocationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedProvince, setSelectedProvince] = useState(formData.mapAddress?.province || '');
    const [districts, setDistricts] = useState([]);
    const [municipalities, setMunicipalities] = useState([]);
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRef = useRef(null);
    const initializedRef = useRef(false);
    const geocodingInProgressRef = useRef(false);

    useEffect(() => {
        const initLocationData = async () => {
            try {
                setLoading(true);
                const response = await fetch('/json/location.json');
                if (!response.ok) throw new Error('Failed to load location data');
                const data = await response.json();
                setLocationData(data.item);
            } catch (error) {
                console.error('Error loading location data:', error);
            } finally {
                setLoading(false);
            }
        };
        initLocationData();
    }, []);

    // Disable body scroll when modal is open
    useEffect(() => {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        document.documentElement.style.overflow = 'hidden';
        
        return () => {
            document.body.style.overflow = 'unset';
            document.body.style.paddingRight = '0px';
            document.documentElement.style.overflow = 'unset';
        };
    }, []);

    // Initialize Google Maps
    useEffect(() => {
        if (loading || !mapRef.current || initializedRef.current) return;

        const initializeMap = async () => {
            try {
                const apiKey = config.googleMaps.apiKey;
                if (!apiKey || apiKey === 'YOUR_GOOGLE_MAPS_API_KEY_HERE') {
                    console.error('Google Maps API key not configured');
                    return;
                }

                await loadGoogleMaps(apiKey);
                console.log('Google Maps loaded successfully');

                // Use Kathmandu as default center if no coordinates exist
                const defaultKathmanduCenter = { lat: 27.7172, lng: 85.3240 };
                const center = (formData.latitude && formData.longitude)
                    ? { lat: parseFloat(formData.latitude), lng: parseFloat(formData.longitude) }
                    : (mapCenter && mapCenter.length === 2 
                        ? { lat: mapCenter[0], lng: mapCenter[1] } 
                        : defaultKathmanduCenter);

                mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
                    center: center,
                    zoom: config.googleMaps.defaultZoom,
                    mapTypeId: window.google.maps.MapTypeId.ROADMAP,
                    streetViewControl: false,
                    fullscreenControl: false,
                    mapTypeControl: false,
                    zoomControl: true,
                    zoomControlOptions: {
                        position: window.google.maps.ControlPosition.RIGHT_BOTTOM
                    }
                });

                // Add click handler
                mapInstanceRef.current.addListener('click', (e) => {
                    if (geocodingInProgressRef.current) {
                        console.log('Geocoding already in progress, ignoring click');
                        return;
                    }
                    
                    const lat = e.latLng.lat();
                    const lng = e.latLng.lng();
                    console.log('Map clicked at:', lat, lng);
                    
                    geocodingInProgressRef.current = true;
                    
                    // Update marker immediately
                    updateMarker(lat, lng);
                    
                    // Update coordinates and do reverse geocoding
                    getLocationFromCoordinates(lat, lng, false).finally(() => {
                        geocodingInProgressRef.current = false;
                    });
                });

                // If coordinates exist, update marker
                if (formData.latitude && formData.longitude) {
                    const correctedLat = getCorrectLatitude(formData.latitude, formData.longitude);
                    const correctedLng = getCorrectLongitude(formData.latitude, formData.longitude);
                    updateMarker(correctedLat, correctedLng);
                    
                    // Only do reverse geocoding if we don't have location data AND generalAddress is empty
                    if (!formData.mapAddress?.province && 
                        !formData.mapAddress?.district && 
                        !formData.mapAddress?.municipality && 
                        !formData.mapAddress?.generalAddress) {
                        console.log('Doing initial reverse geocoding with address preservation');
                        getLocationFromCoordinates(correctedLat, correctedLng, true);
                    }
                } else {
                    // No coordinates set - auto-fill with Kathmandu location
                    console.log('No coordinates found, auto-filling with Kathmandu location');
                    const kathmanduLat = 27.7172;
                    const kathmanduLng = 85.3240;
                    updateMarker(kathmanduLat, kathmanduLng);
                    
                    geocodingInProgressRef.current = true;
                    getLocationFromCoordinates(kathmanduLat, kathmanduLng, false).finally(() => {
                        geocodingInProgressRef.current = false;
                    });
                }

                initializedRef.current = true;
            } catch (error) {
                console.error('Error initializing Google Maps:', error);
            }
        };

        initializeMap();
    }, [loading]);

    // Pan map when mapCenter changes (from search)
    useEffect(() => {
        if (mapInstanceRef.current && mapCenter && mapCenter.length === 2) {
            console.log('Panning map to:', mapCenter);
            mapInstanceRef.current.panTo({ lat: mapCenter[0], lng: mapCenter[1] });
            updateMarker(mapCenter[0], mapCenter[1]);
        }
    }, [mapCenter]);

    const updateMarker = (lat, lng) => {
        if (!mapInstanceRef.current) return;

        // Remove existing marker
        if (markerRef.current) {
            markerRef.current.setMap(null);
        }

        // Add new marker
        markerRef.current = new window.google.maps.Marker({
            position: { lat, lng },
            map: mapInstanceRef.current,
            draggable: true,
            animation: window.google.maps.Animation.DROP
        });

        // Add drag listener
        markerRef.current.addListener('dragend', (e) => {
            if (geocodingInProgressRef.current) {
                console.log('Geocoding already in progress, ignoring drag');
                return;
            }
            
            const newLat = e.latLng.lat();
            const newLng = e.latLng.lng();
            console.log('Marker dragged to:', newLat, newLng);
            
            geocodingInProgressRef.current = true;
            getLocationFromCoordinates(newLat, newLng, false).finally(() => {
                geocodingInProgressRef.current = false;
            });
        });
        
        // Pan to new location without changing zoom
        mapInstanceRef.current.panTo({ lat, lng });
    };

    // When province changes, update districts
    useEffect(() => {
        if (formData.mapAddress?.province && locationData?.provinces) {
            const provinceObj = locationData.provinces.find(
                p => String(p.code) === String(formData.mapAddress.province)
            );
            if (provinceObj && provinceObj.districts) {
                setDistricts(provinceObj.districts);
                // Update selectedProvince to sync with formData
                setSelectedProvince(formData.mapAddress.province);
            }
        }
    }, [formData.mapAddress?.province, locationData]);

    // Initialize Google Places Autocomplete after map loads
    useEffect(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
            autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
            // Only initialize PlacesService if mapInstanceRef is a valid DOM element
            if (mapInstanceRef.current && mapInstanceRef.current instanceof HTMLElement) {
                placesServiceRef.current = new window.google.maps.places.PlacesService(mapInstanceRef.current);
                console.log('Google Places services initialized');
            } else {
                console.log('Map instance not ready, PlacesService will be initialized on demand');
            }
        }
    }, []);

    // Handle search suggestions using Google Places API
    const handleSearchInput = (query) => {
        onSearchChange(query);
        
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        
        if (query.trim().length > 2) {
            setShowSuggestions(true);
            searchTimeoutRef.current = setTimeout(async () => {
                try {
                    if (!autocompleteServiceRef.current) {
                        console.log('Autocomplete service not ready');
                        return;
                    }

                    // Use Google Places Autocomplete
                    const request = {
                        input: query,
                        componentRestrictions: { country: 'np' }, // Restrict to Nepal
                        types: ['geocode', 'establishment']
                    };

                    autocompleteServiceRef.current.getPlacePredictions(request, (predictions, status) => {
                        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                            console.log('Google Places predictions:', predictions);
                            setSearchSuggestions(predictions);
                        } else {
                            console.log('No predictions found or error:', status);
                            setSearchSuggestions([]);
                        }
                    });
                } catch (error) {
                    console.error('Error fetching suggestions:', error);
                    setSearchSuggestions([]);
                }
            }, 300);
        } else {
            setShowSuggestions(false);
            setSearchSuggestions([]);
        }
    };

    const handleSuggestionClick = async (suggestion) => {
        console.log('Suggestion clicked:', suggestion);
        
        // Update search query
        onSearchChange(suggestion.description);
        setShowSuggestions(false);
        
        // Get place details to get coordinates
        // Initialize PlacesService on demand if not already initialized
        if (!placesServiceRef.current && mapInstanceRef.current && mapInstanceRef.current instanceof HTMLElement) {
            placesServiceRef.current = new window.google.maps.places.PlacesService(mapInstanceRef.current);
        }

        if (placesServiceRef.current && suggestion.place_id) {
            const request = {
                placeId: suggestion.place_id,
                fields: ['geometry', 'formatted_address', 'address_components']
            };

            placesServiceRef.current.getDetails(request, (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && place.geometry) {
                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();
                    
                    console.log('Place details:', { lat, lng, address: place.formatted_address });
                    
                    // Pan map and get location details
                    if (!isNaN(lat) && !isNaN(lng)) {
                        getLocationFromCoordinates(lat, lng);
                    }
                } else {
                    console.error('Error getting place details:', status);
                }
            });
        }
    };

    // When district changes, update municipalities
    useEffect(() => {
        if (formData.mapAddress?.district && locationData?.provinces) {
            let districtObj = null;
            locationData.provinces.forEach(province => {
                const found = province.districts?.find(d => String(d.code) === String(formData.mapAddress?.district));
                if (found) districtObj = found;
            });
            
            if (districtObj && districtObj.municipalities) {
                setMunicipalities(districtObj.municipalities);
            } else {
                setMunicipalities([]);
            }
        } else {
            setMunicipalities([]);
        }
    }, [formData.mapAddress?.district, locationData]);

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl p-8 backdrop-blur-xl border border-white/20 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold text-white">Pick Location</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="space-y-4">
                    {/* Search Box */}
                    <div className="relative">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => handleSearchInput(e.target.value)}
                            placeholder="Search any location in Nepal..."
                            className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-400"
                        />
                        
                        {/* Search Suggestions Dropdown */}
                        {showSuggestions && searchSuggestions.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-white/30 rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
                                {searchSuggestions.map((suggestion, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        className="w-full text-left px-4 py-3 hover:bg-slate-700 text-white text-sm border-b border-white/10 last:border-b-0 transition flex items-start gap-3"
                                    >
                                        <div className="text-gray-400 mt-1">📍</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium truncate">{suggestion.main_text || suggestion.description}</div>
                                            <div className="text-gray-400 text-xs truncate">{suggestion.secondary_text || suggestion.description}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Map */}
                    <div className="relative w-full h-96 bg-gray-800 rounded-lg overflow-hidden border border-white/20">
                        {loading ? (
                            <div className="flex items-center justify-center h-full">
                                <p className="text-gray-400">Loading map...</p>
                            </div>
                        ) : (
                            <div ref={mapRef} style={{ height: '100%', width: '100%' }} />
                        )}
                    </div>

                    {/* Coordinates */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-300 font-semibold mb-2">Latitude</label>
                            <input
                                type="number"
                                value={getCorrectLatitude(formData.latitude, formData.longitude) || ''}
                                onChange={(e) => onCoordinateChange('latitude', e.target.value)}
                                step="0.0001"
                                className="w-full px-4 py-2 bg-white/10 border border-white/30 rounded-lg text-white focus:outline-none focus:border-green-400"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-300 font-semibold mb-2">Longitude</label>
                            <input
                                type="number"
                                value={getCorrectLongitude(formData.latitude, formData.longitude) || ''}
                                onChange={(e) => onCoordinateChange('longitude', e.target.value)}
                                step="0.0001"
                                className="w-full px-4 py-2 bg-white/10 border border-white/30 rounded-lg text-white focus:outline-none focus:border-green-400"
                            />
                        </div>
                    </div>

                    {/* Province/District/Municipality Cascading Dropdowns */}
                    <div className="bg-white/5 rounded-lg p-4 space-y-4">
                        <div>
                            <label className="block text-gray-300 font-semibold mb-2">Province *</label>
                            <select
                                value={selectedProvince}
                                onChange={(e) => {
                                    const newProvince = e.target.value;
                                    setSelectedProvince(newProvince);
                                    onAddressChange('province', newProvince);
                                    onAddressChange('district', '');
                                    onAddressChange('municipality', '');
                                }}
                                className="w-full px-4 py-2 bg-white/10 border border-white/30 rounded-lg text-white focus:outline-none focus:border-green-400"
                            >
                                <option value="">Select Province</option>
                                {locationData?.provinces?.map(p => (
                                    <option key={p.code} value={p.code}>
                                        {p.label} - {p.alt_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-gray-300 font-semibold mb-2">District {!selectedProvince && <span className="text-gray-500">(Select Province first)</span>}</label>
                            <select
                                value={formData.mapAddress?.district || ''}
                                onChange={(e) => {
                                    onAddressChange('district', e.target.value);
                                    onAddressChange('municipality', '');
                                }}
                                disabled={!selectedProvince}
                                className="w-full px-4 py-2 bg-white/10 border border-white/30 rounded-lg text-white focus:outline-none focus:border-green-400 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <option value="">Select District</option>
                                {districts.map(d => (
                                    <option key={d.code} value={d.code}>
                                        {d.label} - {d.alt_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-gray-300 font-semibold mb-2">Municipality {!formData.mapAddress?.district && <span className="text-gray-500">(Select District first)</span>}</label>
                            <select
                                value={formData.mapAddress?.municipality || ''}
                                onChange={(e) => onAddressChange('municipality', e.target.value)}
                                disabled={!formData.mapAddress?.district}
                                className="w-full px-4 py-2 bg-white/10 border border-white/30 rounded-lg text-white focus:outline-none focus:border-green-400 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <option value="">Select Municipality</option>
                                {municipalities.map(m => (
                                    <option key={m.code} value={m.code}>
                                        {m.label} - {m.alt_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded-lg transition-all"
                    >
                        Confirm Location
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LocationPickerModal;
